import type { FirebaseOptions } from 'firebase/app';

export type ICountry = 'SE' | 'NO' | 'GB' | 'FR';
export type IBrand = 'KRY' | 'LIVI';
export type IDomain =
  | 'kry.se'
  | 'kry.no'
  | 'kry.co.uk'
  | 'kry.fr'
  | 'embeddable.livi.it'
  | LimeDomain

  // Universal docker-compose domains.
  | 'localhost.se'
  | 'localhost.no'
  | 'localhost.co.uk'
  | 'localhost.fr';

const limeEnvs = {
  test: 'livi.member.test.lime.global',
  staging: 'livi.member.stage.lime.global',
  production: 'livi.member.limeoncall.co.uk',
} as const;
const limeDomains = Object.values(limeEnvs);
type LimeDomain = typeof limeDomains[number];

const getProtocol = () => {
  return `${window.location.protocol}//`;
};

const getDomain = (): IDomain => {
  const host = window.location.host.toLowerCase();
  const envCountry = process.env.REACT_APP_FORCE_COUNTRY;

  // Getting the domain from a url is surprisingly difficult, given multiple (or no) subdomains,
  // second-level domains etc, it's just not worth the hassle.
  switch (true) {
    case host.includes('kry.se') || envCountry === 'SE':
      return 'kry.se';
    case host.includes('kry.no') || envCountry === 'NO':
      return 'kry.no';
    case host.includes('kry.co.uk') || envCountry === 'GB':
      return 'kry.co.uk';

    // Host-only: these are partner hosts with no local force-country
    // equivalent, so GB/FR above claim the env-driven runs.
    case host.includes('embeddable.livi.it'):
      return 'embeddable.livi.it';
    case limeDomains.some((d) => host === d) || envCountry === 'GB':
      return host as LimeDomain;

    case host.includes('kry.fr') || envCountry === 'FR':
      return 'kry.fr';

    // Universal docker-compose domains.
    case host.includes('localhost.se'):
      return 'localhost.se';
    case host.includes('localhost.no'):
      return 'localhost.no';
    case host.includes('localhost.co.uk'):
      return 'localhost.co.uk';
    case host.includes('localhost.fr'):
      return 'localhost.fr';

    default:
      return 'kry.se';
  }
};

// Every domain declared once, so the country and app lists can't drift apart.
// `app` is which mobile app and which Kry/Livi tracking account sit behind the
// market. Every site now presents the Kry brand, but the UK/FR rebrand didn't
// merge the two apps, so kry.co.uk and kry.fr still sit on top of the Livi one.
const domainConfigs: Record<IDomain, { country: ICountry; app: IBrand }> = {
  'kry.se': { country: 'SE', app: 'KRY' },
  'kry.no': { country: 'NO', app: 'KRY' },
  'kry.co.uk': { country: 'GB', app: 'LIVI' },
  'kry.fr': { country: 'FR', app: 'LIVI' },
  'embeddable.livi.it': { country: 'GB', app: 'LIVI' },
  [limeEnvs.test]: { country: 'GB', app: 'LIVI' },
  [limeEnvs.staging]: { country: 'GB', app: 'LIVI' },
  [limeEnvs.production]: { country: 'GB', app: 'LIVI' },

  // Universal docker-compose domains.
  'localhost.se': { country: 'SE', app: 'KRY' },
  'localhost.no': { country: 'NO', app: 'KRY' },
  'localhost.co.uk': { country: 'GB', app: 'LIVI' },
  'localhost.fr': { country: 'FR', app: 'LIVI' },
};

const getDomainConfig = () => domainConfigs[getDomain()];

const getCountry = (): ICountry => getDomainConfig().country;

const isKryApp = () => getDomainConfig().app === 'KRY';

const isDevEnvironment = () => {
  return process.env.NODE_ENV !== 'production';
};

const isProdEnvironment = () => {
  const host = window.location.host.toLowerCase();
  return (
    !isDevEnvironment() &&
    !host.includes('test') &&
    !host.includes('embeddable.livi.it') &&
    !host.includes(limeEnvs.test) &&
    !host.includes(limeEnvs.staging) &&
    !host.includes('localhost') &&
    !host.includes('amplifyapp')
  );
};

const getApiUrlForSubdomain = (subdomain: string, path: string) => {
  return `${getProtocol()}${subdomain}.${getDomain()}${path}`;
};

// PLACEHOLDER pending Infra (Phase 2 CAPTCHA step-up, PAW-2674).
// Each AWS WAF Web ACL is per-market (`prod` for GB, `prod-fr` for FR) and
// exposes its own Application Integration URL, which Terraform doesn't
// surface as an attribute — Infra retrieves it via
// `aws wafv2 get-web-acl --scope REGIONAL --name <acl> --id <id>`
// (`ApplicationIntegrationURL` field) and is currently blocked on AWS
// credentials to do so. Fill in the two real URLs (each `<integration
// URL>/challenge.js`) once Infra provides them. Until then this
// intentionally resolves to `undefined`, so the step-up flow it gates
// stays inert rather than pointing `<script src>` at a guess.
const WAF_CHALLENGE_SCRIPT_URL_PLACEHOLDERS: Partial<Record<ICountry, string>> =
  {
    // FR: '<prod-fr Application Integration URL>/challenge.js',
    // GB: '<prod Application Integration URL>/challenge.js',
  };

const getWafChallengeScriptUrl = (): string | undefined =>
  WAF_CHALLENGE_SCRIPT_URL_PLACEHOLDERS[getCountry()];

// Firebase Web app config (Firebase App Check for SMS-signup protection,
// PAW-2761/PAW-2765). Not a secret — this ships in every client bundle by
// design; Firebase enforces access via App Check + security rules, not by
// hiding this config. `measurementId`/`databaseURL`/`storageBucket` aren't
// used by App Check itself but are harmless to keep alongside the rest of
// the SDK snippet Firebase generated.
const FIREBASE_CONFIG: FirebaseOptions = {
  apiKey: 'AIzaSyCYj5W73C6Brfj_3F50V7YUZVdXkJO8YDY',
  authDomain: 'livi-513b7.firebaseapp.com',
  databaseURL: 'https://livi-513b7.firebaseio.com',
  projectId: 'livi-513b7',
  storageBucket: 'livi-513b7.firebasestorage.app',
  messagingSenderId: '335521983941',
  appId: '1:335521983941:web:5c14d437b40cdb34d8afac',
  measurementId: 'G-S6BW0CKF92',
};

// Not market-gated: only France currently presents the phone-input view
// this powers (SE/NO use BankID/OIDC, GB's Vitality-partnered flow doesn't
// route through it either), so which markets actually exercise App Check is
// already determined by which UI renders, not by a check here. Keeping this
// unconditional means a future market that starts presenting the phone
// input view gets App Check automatically instead of silently missing it.
const getFirebaseConfig = (): FirebaseOptions | undefined => FIREBASE_CONFIG;

// reCAPTCHA Enterprise site key (Firebase App Check for SMS-signup
// protection, PAW-2761/PAW-2765). Registered for all four FR/GB domains
// (livi.fr, kry.fr, livi.co.uk, kry.co.uk). Not a secret — same discipline
// as FIREBASE_CONFIG above (domain-restricted, not secrecy-restricted).
const RECAPTCHA_ENTERPRISE_SITE_KEY: string | undefined =
  '6LebpKYtAAAAACb7ItUrdbmmYT2W_oWPz53DOwKN';

const getRecaptchaEnterpriseSiteKey = (): string | undefined =>
  RECAPTCHA_ENTERPRISE_SITE_KEY;

const environment = {
  DOMAIN: getDomain(),
  COUNTRY: getCountry(),

  IS_KRY_APP: isKryApp(),

  IS_DEV: isDevEnvironment(),
  IS_PROD: isProdEnvironment(),

  WAF_CHALLENGE_SCRIPT_URL: getWafChallengeScriptUrl(),

  FIREBASE_CONFIG: getFirebaseConfig(),
  RECAPTCHA_ENTERPRISE_SITE_KEY: getRecaptchaEnterpriseSiteKey(),

  getApiUrlForSubdomain,
};

export default environment;
